// ORCA360 - Mobile Progress Overlay
// Full-screen overlay shown during long-running operations (transcription, assessment generation)

interface MobileProgressOverlayProps {
  visible: boolean;
  title: string;
  subtitle?: string;
  progress?: number; // 0-100, undefined for indeterminate
  statusText?: string;
}

export default function MobileProgressOverlay({
  visible,
  title,
  subtitle,
  progress,
  statusText,
}: MobileProgressOverlayProps) {
  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-navy/95">
      {/* Animated ring */}
      <div className="relative w-28 h-28 mb-8">
        <svg className="w-full h-full" viewBox="0 0 120 120">
          {/* Background ring */}
          <circle
            cx="60"
            cy="60"
            r="52"
            fill="none"
            stroke="rgba(255,255,255,0.1)"
            strokeWidth="6"
          />
          {/* Progress ring */}
          <circle
            cx="60"
            cy="60"
            r="52"
            fill="none"
            stroke="#4A90E2"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={`${2 * Math.PI * 52}`}
            strokeDashoffset={
              progress !== undefined
                ? `${2 * Math.PI * 52 * (1 - progress / 100)}`
                : `${2 * Math.PI * 52 * 0.75}`
            }
            style={{
              transformOrigin: 'center',
              transform: 'rotate(-90deg)',
              transition: progress !== undefined ? 'stroke-dashoffset 0.5s ease' : 'none',
              animation: progress === undefined ? 'spin 1.5s linear infinite' : 'none',
            }}
          />
        </svg>

        {/* Center text */}
        <div className="absolute inset-0 flex items-center justify-center">
          {progress !== undefined ? (
            <span className="text-2xl font-bold text-white">{Math.round(progress)}%</span>
          ) : (
            <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          )}
        </div>
      </div>

      {/* Title */}
      <h2 className="text-xl font-bold text-white mb-2 text-center px-8">{title}</h2>

      {/* Subtitle */}
      {subtitle && (
        <p className="text-sm text-white/60 text-center px-8 mb-4">{subtitle}</p>
      )}

      {/* Status text */}
      {statusText && (
        <div className="mt-4 px-6 py-2 rounded-full bg-white/10">
          <p className="text-xs text-white/80 font-medium">{statusText}</p>
        </div>
      )}

      {/* Keep screen on notice */}
      <p className="absolute bottom-8 text-xs text-white/30 px-8 text-center pb-safe">
        Please keep this screen open
      </p>

      <style>{`
        @keyframes spin {
          from { transform: rotate(-90deg); }
          to { transform: rotate(270deg); }
        }
      `}</style>
    </div>
  );
}
